import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useStoresQuery, useStoresSummaryQuery } from '@/client/api/stores/stores.query';
import type { AdminStoreListParams } from '@/client/types/stores';

import Pagination from '@/client/components/common/Pagination';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useDebouncedSearch } from '@/client/hooks/useDebouncedSearch';

import { DEFAULT_SORT, StoresFilters, isStoreSort, type PlanFilter, type StoreView } from './StoreList/StoresFilters';
import { StoresGrid } from './StoreList/StoresGrid';
import { StoresSummary } from './StoreList/StoresSummary';
import { StoresTable } from './StoreList/StoresTable';
import { useStoreQuickActions } from './StoreList/StoreQuickActions';
import { parseSegments, segmentParams, toggleSegment } from './StoreList/segments';

const VIEW_KEY = 'ff-admin:stores:view';

const readView = (): StoreView => {
  try {
    return localStorage.getItem(VIEW_KEY) === 'list' ? 'list' : 'cards';
  } catch {
    return 'cards';
  }
};

const StoreListPage = () => {
  useBreadcrumbs([{ label: 'Stores', href: '#' }, { label: 'All Stores' }]);
  const navigate = useNavigate();

  // Filters live in the URL so a filtered roster can be shared and survives
  // the trip into a store and back.
  const [searchParams, setSearchParams] = useSearchParams();
  const patch = (changes: Record<string, string | null>, keepPage = false) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(changes)) {
          if (v) next.set(k, v);
          else next.delete(k);
        }
        if (!keepPage) next.delete('page');
        return next;
      },
      { replace: true },
    );

  const page = Math.max(1, Number(searchParams.get('page')) || 1);
  const limit = Math.min(50, Math.max(10, Number(searchParams.get('limit')) || 20));
  const q = searchParams.get('q') ?? '';
  const segments = useMemo(() => parseSegments(searchParams.get('seg')), [searchParams]);
  const planRaw = searchParams.get('plan');
  const plan: PlanFilter = planRaw === '0' || planRaw === '1' ? planRaw : '';
  const planTier = searchParams.get('tier') ?? '';
  const owner = searchParams.get('owner') ?? '';
  const sortRaw = searchParams.get('sort');
  const sort = isStoreSort(sortRaw) ? sortRaw : DEFAULT_SORT;

  const { value: search, setValue: setSearch, debounced, isDebouncing } = useDebouncedSearch(q, { delay: 400 });
  useEffect(() => {
    if (debounced !== q) patch({ q: debounced || null });
    // Only the settled search term should write to the URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);

  const ownerBox = useDebouncedSearch(owner, { delay: 400 });
  useEffect(() => {
    if (ownerBox.debounced !== owner) patch({ owner: ownerBox.debounced || null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerBox.debounced]);

  const [view, setView] = useState<StoreView>(readView);
  const changeView = (next: StoreView) => {
    setView(next);
    try {
      localStorage.setItem(VIEW_KEY, next);
    } catch {
      // Private mode: the choice just doesn't persist.
    }
  };

  // Search, owner and plan narrow the tiles' counts; the tiles themselves do not, so
  // each count stays "how many would I get if I clicked this".
  const scope = useMemo<AdminStoreListParams>(() => {
    const next: AdminStoreListParams = {};
    if (q) next.search = q;
    if (owner) next.owner = owner;
    if (plan) next.has_plan = plan === '1' ? 1 : 0;
    if (planTier.trim()) next.plan_tier = planTier.trim();
    return next;
  }, [q, owner, plan, planTier]);

  const params = useMemo<AdminStoreListParams>(() => {
    const [by, dir] = sort.split(':') as [NonNullable<AdminStoreListParams['sort']>, 'asc' | 'desc'];
    return { ...scope, ...segmentParams(segments), limit, offset: (page - 1) * limit, sort: by, order: dir };
  }, [scope, segments, limit, page, sort]);

  const { data, isLoading, isFetching, error } = useStoresQuery(params);
  const { data: summary } = useStoresSummaryQuery(scope);
  const rows = useMemo(() => data?.stores ?? [], [data]);
  const total = data?.total_count ?? rows.length;

  useEffect(() => {
    const lastPage = Math.max(1, Math.ceil(total / limit));
    if (data && page > lastPage) patch({ page: lastPage > 1 ? String(lastPage) : null }, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total, limit, page, data]);

  const { request: requestAction, dialog } = useStoreQuickActions();
  const open = (storeid: number) => navigate(`/admin/stores/${storeid}`);
  const filtered = !!(q || owner || plan || planTier || segments.length || sort !== DEFAULT_SORT);

  return (
    <div className="min-h-screen bg-[#f1f2f6] px-4 py-6 sm:px-6">
      <div className="mx-auto w-full max-w-[1320px]">
        <div className="mb-5 flex items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Stores</h1>
            <p className="mt-1 text-sm text-gray-500">Manage, moderate and regulate seller stores</p>
          </div>
          <span className="whitespace-nowrap text-sm text-gray-500">
            {total.toLocaleString()} {filtered ? 'matching' : ''} store{total === 1 ? '' : 's'}
          </span>
        </div>

        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            Couldn’t load stores — {(error as Error).message}
          </div>
        )}

        <StoresSummary
          summary={summary}
          active={segments}
          onToggle={(key) => patch({ seg: toggleSegment(segments, key).join(',') || null })}
          onClear={() => patch({ seg: null })}
        />

        <StoresFilters
          search={search}
          onSearch={setSearch}
          searchBusy={isDebouncing || ownerBox.isDebouncing || isFetching}
          plan={plan}
          onPlan={(v) => patch({ plan: v || null })}
          owner={ownerBox.value}
          onOwner={ownerBox.setValue}
          planTier={planTier}
          planTiers={summary?.plan_tiers}
          onPlanTier={(v) => patch({ tier: v || null })}
          sort={sort}
          onSort={(v) => patch({ sort: v === DEFAULT_SORT ? null : v })}
          view={view}
          onView={changeView}
          onReset={filtered ? () => { setSearch(''); ownerBox.setValue(''); setSearchParams({}, { replace: true }); } : undefined}
        />

        {view === 'list'
          ? <StoresTable rows={rows} loading={isLoading} onOpen={open} onAction={requestAction} />
          : <StoresGrid rows={rows} loading={isLoading} onOpen={open} onAction={requestAction} />}

        <div className="mt-4 rounded-lg border border-gray-200 bg-white px-4 py-3 sm:px-6">
          <Pagination
            total={total}
            page={page}
            itemsPerPage={limit}
            limit={limit}
            setPage={(next: number) => patch({ page: next > 1 ? String(next) : null }, true)}
            setItemsPerPage={(next: number) => patch({ limit: next === 20 ? null : String(next) })}
            showButtons
            showSelect
            selectOptions={[10, 20, 30, 40, 50]}
          />
          {isFetching && !isLoading && <p className="mt-1 text-xs text-gray-400">Refreshing…</p>}
        </div>
      </div>

      {dialog}
    </div>
  );
};

export default StoreListPage;
